// Knowledge Base payload types.
//
// The article register follows phase 27 (artboard 16a) — re-cut for the data
// this deployment actually has, so it needs no migration. The gap / Ask /
// category / document types below are phase 25 §8 and land with their screens.
//
// Invariants:
//
//   `failureMode` drives `suggestedAction` — the client never guesses which
//   verb a gap deserves.
//
//   A failed document's reason is a human sentence, rendered verbatim.
//
//   `helpful` and `rated` travel together wherever they DO exist (Ask sources)
//   — a percentage without its denominator is unusable.

export type ArticleStatus = 'published' | 'draft' | 'archived';

/**
 * The register row — ONLY fields the list endpoint already returns.
 *
 * There is no `helpful`/`rated` pair here on purpose (phase 27 §0). Nothing in
 * this deployment collects ratings, so the column read `no ratings` on every
 * row, cost 150px of the widest table in the app, and taught the reader to skip
 * that region — so the day a real `38% · 11 OF 29` lands there, nobody looks.
 * An empty column is not a placeholder; it is a habit being taught. §11 of the
 * plan puts it back between STATUS and VIEWS when ratings exist.
 *
 * `isIndexed` replaces the three-state AI column for the same reason: a boolean
 * cannot answer "is this used by the AI", so the column asks what it can
 * answer — is this in the index.
 */
export interface ArticleRow {
  id: string;
  title: string;
  slug: string;
  category: string | null;
  author: string | null;
  status: ArticleStatus;
  /** With helpfulness gone, the only number saying anyone read this. */
  views: number;
  /** ISO — the client derives the age from it. */
  updatedAt: string;
  isIndexed: boolean;
}

/** The one new number in the phase, and it is a constant, not a column. */
export const STALE_AFTER_DAYS = 60;

export function daysSince(iso: string): number {
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? Math.floor((Date.now() - t) / 86_400_000) : 0;
}

export function isStale(iso: string): boolean {
  return daysSince(iso) > STALE_AFTER_DAYS;
}

/** '18 Aug 2026'. */
export function reviewDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export type GapFailureMode = 'zero_results' | 'no_click_through' | 'human_answered';
export type GapAction = 'draft' | 'extend' | 'ingest';

export interface Gap {
  id: string;
  question: string;
  priority: 'high' | 'medium' | 'low';
  timesAsked: number;
  bySource: { search: number; chat: number; voice: number; email: number };
  failureMode: GapFailureMode;
  /** 'SEARCH 21 · CHAT 9 · ZERO RESULTS EVERY TIME' — shown verbatim. */
  evidenceLine: string;
  diagnosis: string;
  suggestedAction: GapAction;
  extendTargetId: string | null;
  lastAskedAt: string;
}

export interface AskSource {
  index: number;
  id: string;
  title: string;
  score: number;
  kind: 'article' | 'document';
  page?: number;
  /** The source's OWN helpfulness, beside its retrieval score (§5). */
  helpful: number | null;
  rated: number | null;
}

export interface AskResult {
  answer: { text: string; citations: { index: number; sourceId: string }[] };
  sources: AskSource[];
  latencyMs: number | null;
  chunksUsed: number | null;
  chunksTotal: number | null;
  alsoAsked: string[];
}

export interface CategoryNode {
  id: string;
  name: string;
  slug: string;
  icon: string | null;
  parentId: string | null;
  position: number;
  articleCount: number;
  childCount: number;
  visibility: 'public' | 'internal' | 'inactive';
  /** > 0 → the warn meta line. Gaps surface in the tree. */
  openGaps: number;
}

/**
 * What the ingest pipeline has done to a file.
 *
 * `done`/`total` are OPTIONAL on purpose. The designed chip reads
 * `EXTRACTING 12/48`, but `KnowledgeDocument` carries no progress counter —
 * so the counter renders when a backend supplies one and the chip says
 * `EXTRACTING` alone when it does not, rather than assembling a fraction out
 * of nothing (18f).
 */
export type DocumentState =
  | { kind: 'ready' }
  | { kind: 'extracting'; done?: number; total?: number }
  | { kind: 'failed'; reason: string }
  | { kind: 'excluded' };

export interface DocumentRow {
  id: string;
  title: string;
  fileName: string;
  category: string | null;
  state: DocumentState;
  pages: number | null;
  sizeBytes: number;
  citedCount: number | null;
}
